import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { DepartmentDisplay } from '@modules/department/models/department-display';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { LicenseState, limitedAccessLicenseStates, supportAccessLicenseStates } from '@pixacare/domain';
import { RedirectionService } from '@services/redirection.service';
import {
  selectIsDepartmentStateLoaded,
  selectUserEnabledClientDepartments,
} from '@shared/store/departments/departments.selectors';
import { combineLatest, distinctUntilChanged, filter, map, Observable, shareReplay, switchMap } from 'rxjs';
import { ClientData } from 'src/app/shared/models/base/client-data';
import { selectClients } from 'src/app/shared/store/clients/clients.selectors';
import {
  selectHasLicenseSupportOnlyAccess,
  selectReadAccessClientCodes,
  selectUserActiveLicenseState,
} from 'src/app/shared/store/licenses/licenses.selectors';
import { selectIsStoreInitialized } from 'src/app/shared/store/life-cycle/life-cycle.selectors';
import { selectClientCode } from 'src/app/shared/store/router/router.selectors';
import { environment } from 'src/environments/environment';

@UntilDestroy()
@Injectable()
export class SidebarService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private readonly clientRedirectService = inject(RedirectionService);

  private readonly userId = inject(AuthenticationService).user.id;

  collapsed = false;
  shown = false;

  clients$: Observable<{ [clientCode: string]: ClientData }> = combineLatest({
    clients: this.store.select(selectClients),
    readAccessClientCodes: this.store.select(selectReadAccessClientCodes),
    isAppInitialized: this.store.select(selectIsStoreInitialized),
  }).pipe(
    filter(({ isAppInitialized }) => isAppInitialized),
    map(({ clients, readAccessClientCodes }) =>
      readAccessClientCodes
        .sort((clientCodeA, clientCodeB) => clients[clientCodeA].fullName.localeCompare(clients[clientCodeB].fullName))
        .reduce(
          (acc, clientCode) => ({
            ...acc,
            [clientCode]: clients[clientCode],
          }),
          {},
        ),
    ),
  );

  activeClientCode$: Observable<string> = this.store.select(selectClientCode);

  areDepartmentsLoaded$: Observable<boolean> = this.activeClientCode$.pipe(
    switchMap((clientCode) => this.store.select(selectIsDepartmentStateLoaded(clientCode))),
  );

  activeLicenseState$: Observable<LicenseState> = this.store.select(selectUserActiveLicenseState);

  isActiveLicenseStateLimited$: Observable<boolean> = this.activeLicenseState$.pipe(
    map((licenseState) => limitedAccessLicenseStates.includes(licenseState)),
  );

  shouldDisplayDataConsumption$: Observable<boolean> = combineLatest([
    this.activeClientCode$,
    this.activeLicenseState$,
  ]).pipe(
    map(
      ([activeClientCode, activeLicenseState]) =>
        activeClientCode === environment.commonClientCode || supportAccessLicenseStates.includes(activeLicenseState),
    ),
  );

  departments$: Observable<DepartmentDisplay[]> = combineLatest([
    this.activeClientCode$,
    this.store.select(selectHasLicenseSupportOnlyAccess),
  ]).pipe(
    distinctUntilChanged(),
    switchMap(([clientCode, hasLicenseSupport]) =>
      this.store
        .select(selectUserEnabledClientDepartments(clientCode, this.userId))
        .pipe(
          map((departments) =>
            hasLicenseSupport ? [...departments].sort((a, b) => a.name.localeCompare(b.name)) : departments,
          ),
        ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    // dismiss navbar when clicking on a link on mobile.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.shown = false;
      });
  }

  updateLocation(clientCode: string): void {
    this.clientRedirectService.navigateToDefaultClientPage(clientCode);
  }
}
